import { Box, useTheme } from '@mui/material';
import { AssetType, WidgetSubTypeEnum } from 'src/features/sites/types/types';
import { ValveWidget } from 'src/assets/widgets';
import { ValveScadaIcon } from 'src/assets/icons/SCADA/assetsComponents/ValveScadaIcon';
import { DataStructure } from 'src/features/widgets/types';
import { useValveData } from 'src/features/sites/hooks';
import { useState, useEffect, useMemo } from 'react';
import { ReadingsService } from 'src/services/readings/readings.service';
import { getStateOrModeData } from 'src/features/widgets/utils';
import { STATE } from 'src/features/sites';
import { CustomWaterLoading } from 'src/components';
import { StateLabelBox } from '../../StateLabelBox';

type Props = {
  widgetSubType: string;
  asset: AssetType | undefined;
};

export const ValveAssetControl = ({ widgetSubType, asset }: Props) => {
  const [readings, setReadings] = useState<DataStructure>([]);
  const [isDataReady, setIsDataReady] = useState<boolean>(false);
  const { valveLiveData } = useValveData({ asset: asset! });

  const theme = useTheme();

  const liveState = valveLiveData.state;

  useEffect(() => {
    const uniqueGatewayIds = Array.from(
      new Set(asset?.assignedTags?.map((item) => item.assignedGatewayId).filter(Boolean))
    );
    const requests = uniqueGatewayIds.map(async (gatewayId) => {
      if (!gatewayId) return Promise.resolve();

      const uniqueTagIds: string[] = Array.from(
        new Set(
          asset?.assignedTags
            ?.filter(
              (item) => item.assignedGatewayId === gatewayId && item.assignedTagId !== undefined
            )
            ?.map((item) => item.assignedTagId as string)
        )
      );

      try {
        const res = await ReadingsService.getLatestReadingsNewDataStorage({
          gatewayId: gatewayId,
          measurements: uniqueTagIds,
        });

        if (res !== null) {
          setReadings((prevState) => [...prevState, { gatewayId: gatewayId, values: res.data }]);
        }
      } catch (error) {
        console.error(error);
      }
    });

    Promise.all(requests).then(() => {
      setIsDataReady(true);
    });
  }, [asset]);

  const filteredAllAssetStates = useMemo(
    () =>
      asset?.assignedTags
        ?.filter((tag) => tag.tagName.toLowerCase() === STATE)
        .sort((a, b) => a.tagIndicatorPosition - b.tagIndicatorPosition),
    [asset]
  );

  const displayState = liveState
    ? liveState
    : getStateOrModeData({
        data: filteredAllAssetStates!!,
        latestReadings: readings,
      });

  const renderValveIcon = () => {
    if (widgetSubType === WidgetSubTypeEnum.VALVE) {
      return <ValveWidget width={55} height={70} />;
    }

    if (widgetSubType === WidgetSubTypeEnum.VALVE_1) {
      return (
        <ValveScadaIcon fill={displayState === 'Opened' ? theme.palette.primary.main : '#ECEDEF'} />
      );
    }

    return null;
  };

  return isDataReady ? (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
      }}
    >
      <Box>{renderValveIcon()}</Box>
      {displayState && displayState !== '-' && (
        <StateLabelBox fontSize="11px" padding="1px 7px" text={displayState} />
      )}
    </Box>
  ) : (
    <CustomWaterLoading />
  );
};
